import { forwardRef } from "react";
import type { ComponentPropsWithoutRef, ElementRef, ReactNode } from "react";
import { Label } from "@radix-ui/react-label";
import * as RadioGroup from "@radix-ui/react-radio-group";

import { tw } from "@/utils";

const Root = forwardRef<
  ElementRef<typeof RadioGroup.Root>,
  ComponentPropsWithoutRef<typeof RadioGroup.Root>
>(({ className, ...props }, ref) => (
  <RadioGroup.Root
    ref={ref}
    className={tw("flex flex-col gap-4", className)}
    {...props}
  />
));
Root.displayName = "RadioGroupCard.Root";

interface RadioGroupCardItemProps
  extends ComponentPropsWithoutRef<typeof RadioGroup.Item> {
  children?: ReactNode;
  error?: string;
}

const Item = forwardRef<
  ElementRef<typeof RadioGroup.Item>,
  RadioGroupCardItemProps
>(({ className, children, error, ...props }, ref) => (
  <RadioGroup.Item
    ref={ref}
    className={tw(
      "group flex w-full flex-row items-center gap-x-3 rounded-lg border p-3 transition data-[state=checked]:border-primary-200 data-[disabled]:opacity-50",
      error ? "border-error-500" : "border-neutral-100",
      className,
    )}
    {...props}
  >
    <Indicator />
    <div className="flex flex-col justify-center gap-1">{children}</div>
  </RadioGroup.Item>
));
Item.displayName = "RadioGroupCard.Item";

const Indicator = forwardRef<
  ElementRef<"div">,
  ComponentPropsWithoutRef<"div">
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={tw(
      className,
      "flex size-5 flex-shrink-0 items-center justify-center self-center rounded-full border-2 border-neutral-200 group-data-[disabled]:cursor-not-allowed group-data-[state=checked]:border-primary-500",
    )}
    {...props}
  >
    <RadioGroup.Indicator className="size-2.5 rounded-full bg-primary-500" />
  </div>
));
Indicator.displayName = "RadioGroupCard.Indicator";

const ItemLabel = forwardRef<
  ElementRef<typeof Label>,
  ComponentPropsWithoutRef<typeof Label>
>(({ className, ...props }, ref) => (
  <Label
    ref={ref}
    className={tw("font-medium text-primary-950", className)}
    {...props}
  />
));
ItemLabel.displayName = "RadioGroupCard.ItemLabel";

const GroupLabel = forwardRef<
  ElementRef<typeof Label>,
  ComponentPropsWithoutRef<typeof Label>
>(({ className, ...props }, ref) => (
  <Label
    ref={ref}
    className={tw("col-span-12 font-medium text-primary-950", className)}
    {...props}
  />
));
GroupLabel.displayName = "RadioGroupCard.GroupLabel";

export { Root, Item, Indicator, ItemLabel, GroupLabel };
